/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import {makeStyles, Tooltip} from '@material-ui/core';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {translate} from '@ion/core/src/locale/locale';
import {IUserPreferences} from '@ion/core/src/preferences/types';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import GroupProperties, {
  getUrgentNotificationsEnabled
} from '@ion/core/src/services/GroupProperties';
import Sisense from '@ion/core/src/services/Sisense';
import {setUserTimeZone} from '@ion/core/src/timezone/timezone';
import {
  Account,
  GenericObject,
  Unit,
  User
} from '@ion/core/src/types/entities';
import {
  ApplicationPage
} from '@ion/core/src/types/pages';
import {
  Preference,
  Preferences
} from '@ion/core/src/types/preferences';
import {isEqual} from '@ion/core/src/utils/StringUtil';
import TertiaryButton from '@ion/dls/src/components/buttons/TertiaryButton';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import Dropdown, {Options} from '@ion/dls/src/components/menus/Dropdown';
import DropdownGroup, {Options as DropdownGroupOptions} from '@ion/dls/src/components/menus/DropdownGroup';
import Switch from '@ion/dls/src/components/switch/Switch';
import {NotifictionRowsChooser} from '@ion/dls/src/editors/NotifictionRowsChooser';
import TopNavHelpIcon from '@ion/dls/src/icons/TopNavHelpIcon';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import DateFormatChooser from './DateFormatChooser';
import GroupChooser from './GroupChooser';
import LanguageChooser from './LanguageChooser';
import TimeFormatChooser from './TimeFormatChooser';
import TimezoneChooser from './TimezoneChooser';
import UnitOfMeasureChooser from './UnitOfMeasureChooser';

export type PageNameResolver = (route: string, useTitleCase?: boolean, labelId?: string) => string;

export interface UserPreferencesEditorProps {
  api: IUserPreferences;
  user: User;
  account: Account;
  pages: ApplicationPage[];
  defaultPageId: string;
  pageNameResolver: PageNameResolver;
  accountsMap: GenericObject;
  bottomBorder?: boolean;
  includeDateFormat?: boolean;
  ctcAdmin?: boolean;
  includeTimeFormat?: boolean;
  includeGroupProperties?: boolean;
  omitPreferences?: boolean;
  isNotifications?: boolean;
};

export interface UserPreferencesEditorClient {
  getPreferences: () => Preferences
};

interface GetPageOptionsArgs {
  pages: ApplicationPage[];
  getRouteLabel: PageNameResolver;
  all?: Options[];
  parentName?: string;
};

export const NONE_VALUE = 'none';

const useStyles = makeStyles(theme => ({
  container : {
    margin : '0px !important'
  },
  grids : {
    marginTop   : 38,
    paddingLeft : 10
  },
  timezone : {
    fontSize : 12,
    padding  : 8
  },
  infoLabel : {
    ...dlsAlt('typography', 'label2'),
    color         : dlsColor('grey3'),
    letterSpacing : 'normal'
  },
  preferencesPanel : {
    display         : 'flex',
    justifyContent  : 'space-between',
    backgroundColor : 'white',
    padding         : 10,
    alignItems      : 'center'
  },
  preferencesTitle : {
    color : '#333333'
  },
  preferencesPanelExpanded : {
    backgroundColor : 'white',
    marginTop       : '2px',
    padding         : '10px 20px 10px 20px',
    display         : 'flex',
    flexDirection   : 'column'
  },
  prefernecesTooltip : {
    display    : 'flex',
    alignItems : 'center',
    marginLeft : '6px'
  },
  preferencesPanelRow : {
    width         : '100%',
    display       : 'flex',
    flexDirection : 'row',
    alignItems    : 'center',
    padding       : '10px'
  },
  preferencesPanelCheckbox : {
    width            : '50%',
    '&:nth-child(2)' : {
      paddingLeft : '36px'
    }
  }
}));

export const toggleTooltip = makeStyles(theme => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'tooltip')
  }
}));

/**
 * Manages updating the current Users Preferences. This function is only used when
 * the user being edited is the current user (i.e. the authenticated user).
 */
export const updateUserPreferences = (user: User, preferences: Preferences, api: IUserPreferences): User => {
  const {
    timeZone,
    accountTimeZone,
    unitOfMeasure,
    defaultPageId,
    persistGridLayouts,
    defaultGroupId,
    defaultLanguage,
    showNotifications,
    showUrgentNotifications,
    showGeneralNotifications,
    showTimeRelatedNotifications,
    showConnectivityNotifications,
    defaultDateFormat,
    showScheduledEventsNotifications,
    defaultTimeFormat,
    selectedNotificationsPerPage
  } = preferences;
  if (timeZone !== user.timeZone) {
    setUserTimeZone(timeZone || accountTimeZone);
  }
  if (unitOfMeasure !== user.unitOfMeasure) {
    Sisense.updateSettings({
      unit_of_measurement : unitOfMeasure
    }, true);
  }
  user.timeZone      = timeZone;
  user.unitOfMeasure = unitOfMeasure;
  user.systemOfUnits = {
    value : unitOfMeasure || 'accountDefault'
  };
  api.set(Preference.defaultPageId, defaultPageId);
  api.set(Preference.defaultGroupId, defaultGroupId);

  if (defaultLanguage !== 'default') {
    api.setDefaultLanguage(defaultLanguage);
  } else {
    api.setDefaultLanguage();
  }

  api.set(Preference.defaultDateFormat, defaultDateFormat);
  api.set(Preference.selectedNotificationsPerPage, selectedNotificationsPerPage);
  api.set(Preference.persistGridLayouts, persistGridLayouts);
  api.set(Preference.showNotifications, showNotifications);
  api.set(Preference.showUrgentNotifications, showUrgentNotifications);
  api.set(Preference.showGeneralNotifications, showGeneralNotifications);
  api.set(Preference.showTimeRelatedNotifications, showTimeRelatedNotifications);
  api.set(Preference.showConnectivityNotifications, showConnectivityNotifications);
  api.set(Preference.showScheduledEventsNotifications, showScheduledEventsNotifications);
  api.set(Preference.defaultTimeFormat, defaultTimeFormat);

  user.extendedAttribute = {
    ...user.extendedAttribute,
    value : JSON.stringify(api.getStore())
  };

  return user;
};

/**
 * @private
 *
 * Provides a utility which is used by the UserPreferencesEditor to initialize values
 * based on the the User's current Preferences and / or the given default values.
 */
const getPreferences = (pages: ApplicationPage[], defaultPageId: string, preferences: IUserPreferences) => {
  const defaultPage    = pages?.find(({id}: ApplicationPage): boolean => defaultPageId === id);
  const selectedPageId = preferences.get(Preference.defaultPageId) || defaultPage?.id;
  return {
    saveGridSettingsPreference                     : preferences.get(Preference.persistGridLayouts),
    defaultPageLabel                               : defaultPage?.label || defaultPage?.name,
    selectedPageIdPreference                       : selectedPageId,
    selectedGroupId                                : preferences.get(Preference.defaultGroupId),
    selectedLanguagePreference                     : preferences.getDefaultLanguage(),
    savedDateFormat                                : preferences.get(Preference.defaultDateFormat),
    saveShowNotificationsPreference                : preferences.get(Preference.showNotifications),
    saveShowUrgentNotificationsPreference          : preferences.get(Preference.showUrgentNotifications),
    saveShowGeneralNotificationsPreference         : preferences.get(Preference.showGeneralNotifications),
    saveShowTimeRelatedNotificationsPreference     : preferences.get(Preference.showTimeRelatedNotifications),
    saveShowConnectivityNotificationsPreference    : preferences.get(Preference.showConnectivityNotifications),
    saveShowScheduledEventsNotificationsPreference : preferences.get(Preference.showScheduledEventsNotifications),
    timeZone                                       : preferences.get(Preference.userTimeZone)?.timeZone,
    savedTimeFormat                                : preferences.get(Preference.defaultTimeFormat),
    selectedNotificationsPerPage                   : preferences.get(Preference.selectedNotificationsPerPage)
  };
};

/**
 * @private
 *
 * Creates the iOn Suite Page Options required for editing a users preferences.
 */
const getPageOptions = ({pages, all = [], parentName, getRouteLabel}: GetPageOptionsArgs): Options[] => {
  pages.forEach((page: ApplicationPage) => {
    const {id, isPreview, isAuthorized, route, labelId, pages:children} = page;
    if (id) {
      const name  = getRouteLabel(route, true, labelId);
      const label = (!labelId && parentName) ? `${parentName} ${name}` : name;
      if (children?.length) {
        getPageOptions({
          pages      : children as ApplicationPage[],
          parentName : label,
          getRouteLabel,
          all
        });
      } else if (isPreview && isAuthorized) {
        all.push({label, value: id});
      }
    }
  });
  return all;
};

/**
 *
 * Creates the CTC admin Page Options required for editing a users preferences.
 */
const getCTCPageOptions = (pages, getRouteLabel): DropdownGroupOptions[] => {
  const groupedPages = [];
  pages.forEach((page: ApplicationPage) => {
    const {id, isPreview, isAuthorized, route, pages:children} = page;
    if (id) {
      const label  = getRouteLabel(route);
      if (isPreview && isAuthorized) {
        if (children?.length) {
          const childrenPages = children.map(({id, route}) => {
            const pageLabel = getRouteLabel(route);
            if (!isEqual(pageLabel, 'Report Scheduler')) {
              return {id, label: pageLabel};
            }
          })?.filter(page => !!page);
          groupedPages.push({id, label, childPages: childrenPages});
        } else {
          groupedPages.push({id, label, childPages: [{id, label}]});
        }
      }
    }
  });
  return groupedPages;
};

/**
 * @private
 *
 * Provides a utility which determines the timezone label that is to be displayed.
 */
const getTimeZoneLabel = (selected?: string) => {
  return translate('common:editors.preferences.timezone.title', !selected ? {
    current : `(${translate('common:editors.preferences.timezone.default')})`
  } : null);
};

/**
 * @UserPreferencesEditor
 *
 * The UserPreferences Editor provides the UI which allows a user to update their default
 * application preferences, such as their default page, group, etc..
 *
 * Note: This component is intended to be used directly by the UserEditor and should not
 * be used in stand-alone mode.
 *
 * * ```typescript
 *
 *    import {UserPreferencesEditor} from '@ion/dls/src/editors/';
 *
 *    <UserPreferencesEditor open={editorOpen}
 *      account={account} />
 *
 * ```
 *
 * @param {Account} account The users root level account from which their account hierarchy
 *        will be retrieved.
 * @param {Options} pages A collection of Page Options to be displayed within the landing
 *        page dropdown.
 * @param {string} defaultPageId The id of the application's default page. This prop allows
 *        for setting the users default page (if one has not been selected), as well as for
 *        resetting their default page back to the application default.
 * @param {boolean} persist When true, indicates that the users preference changes should
 *        be persisted to the platform via the UserPreferences API. Initially, this prop
 *        should be set to false, and only set to true when the preferences should be
 *        saved, such as when the user clicks save in the User Profile Dialog, etc..
 */
export const UserPreferencesEditor = forwardRef(({
  api,
  user,
  account,
  pages,
  defaultPageId,
  pageNameResolver,
  accountsMap,
  includeGroupProperties = true,
  includeDateFormat = false,
  bottomBorder = false,
  ctcAdmin = false,
  includeTimeFormat = false,
  omitPreferences = false,
  isNotifications = false
}: UserPreferencesEditorProps, ref) => {
  const classes = useStyles();
  const {
    selectedPageIdPreference,
    saveGridSettingsPreference,
    defaultPageLabel,
    selectedGroupId,
    saveShowNotificationsPreference,
    saveShowUrgentNotificationsPreference,
    saveShowGeneralNotificationsPreference,
    saveShowTimeRelatedNotificationsPreference,
    saveShowConnectivityNotificationsPreference,
    saveShowScheduledEventsNotificationsPreference,
    selectedLanguagePreference,
    timeZone,
    savedTimeFormat,
    savedDateFormat,
    selectedNotificationsPerPage
  }                                                     = getPreferences(pages, defaultPageId, api);
  const [selectedPageId, setSelectedPageId]             = useState<string>(selectedPageIdPreference);
  const [selectedUOM, setSelectedUOM]                   = useState<string>(user.unitOfMeasure);
  const [selectedTimeZone, setSelectedTimeZone]         = useState<string>(timeZone || user.timeZone);
  const [saveGridSettings, setGridSettingsToggle]       = useState<boolean>(saveGridSettingsPreference);
  const [selectedDefaultGroup, setSelectedDefaultGroup] = useState<Account>(accountsMap[selectedGroupId]);
  const [selectedLanguage, setSelectedLanguage]         = useState<string>(selectedLanguagePreference);
  const [showNotifications, setShowNotificationsToggle] = useState<boolean>(saveShowNotificationsPreference);
  const [defaultTimeFormat, setDefaultTimeFormat]       = useState<string>(savedTimeFormat);
  const [showUrgentNotifications, setShowUrgentNotificationsCheckbox] =
    useState<boolean>(saveShowUrgentNotificationsPreference);
  const [showGeneralNotifications, setShowGeneralNotificationsCheckbox] =
    useState<boolean>(saveShowGeneralNotificationsPreference);
  const [showTimeRelatedNotifications, setShowTimeRelatedNotificationsCheckbox] =
    useState<boolean>(saveShowTimeRelatedNotificationsPreference);
  const [showConnectivityNotifications, setShowConnectivityNotificationsCheckbox] =
    useState<boolean>(saveShowConnectivityNotificationsPreference);
  const [showScheduledEventsNotifications, setShowScheduledEventsNotificationsCheckbox] =
    useState<boolean>(saveShowScheduledEventsNotificationsPreference);
  const [isUrgentNotificationsEnabled, setUrgentNotifications] =
    useState(false);
  const [defaultDateFormat, setDefaultDateFormat] = useState<string>(savedDateFormat);
  const [notificationRows, setNotificationRows] =
    useState(selectedNotificationsPerPage || '50');

  /**
   * Returns the UserPreferences public api which exposes a single getPreferences
   * function which is to be invoked when the current state of the users preferences
   * are to be used.
   */
  useImperativeHandle(ref, () => ({
    getPreferences : function() {
      const unitOfMeasure = selectedUOM !== 'Default' ? selectedUOM : null;
      return {
        defaultPageId                : selectedPageId,
        defaultGroupId               : entityId(selectedDefaultGroup),
        defaultLanguage              : selectedLanguage,
        persistGridLayouts           : saveGridSettings,
        timeZone                     : selectedTimeZone,
        selectedNotificationsPerPage : notificationRows,
        showNotifications,
        showUrgentNotifications,
        showGeneralNotifications,
        showTimeRelatedNotifications,
        showConnectivityNotifications,
        showScheduledEventsNotifications,
        unitOfMeasure,
        defaultDateFormat,
        defaultTimeFormat
      };
    }
  }));

  useEffect(() => {
    async function loadCurrentGroupPreferences() {
      if (!ctcAdmin) {
        await GroupProperties.getInheritedProperties(entityId(account?.href));
        setUrgentNotifications(getUrgentNotificationsEnabled());
      }
    }
    loadCurrentGroupPreferences();
  }, []);

  /**
   * Invoked when the user selects their default page preference.
   */
  const onSetPagePreference = function({target}): void {
    setSelectedPageId(target.value);
  };

  /**
   * Invoked when the user selects their default page preference.
   */
  const onSetGroupPagePreference = function(value): void {
    setSelectedPageId(value);
  };
  /**
   * Invoked when the user selects their default group preference.
   */
  const onSelectDefaultGroup = function(group): void {
    setSelectedDefaultGroup(group);
  };

  /**
   * Resets the user's default group to the user's account.
   */
  const onResetDefaultGroup = function(): void {
    setSelectedDefaultGroup(user.account);
  };

  /**
   * Invoked when the user resets their page preference to the application's default page.
   */
  const onResetPagePreference  = useCallback(function(): void {
    setSelectedPageId(defaultPageId);
  }, [defaultPageId]);

  /**
   * Invoked when the user selects a unit of measure (UOM).
   */
  const onSetUOMPreference = function(value: string): void {
    setSelectedUOM(value);
  };

  // invokes when the user selects a specific date format...
  const onSetDateFormatPreference = function(value): void {
    setDefaultDateFormat(value !== NONE_VALUE ? value : null);
  };

  /**
   * Invoked when the user selects a language.
   */
  const onSetLanguagePreference = function(value: string): void {
    setSelectedLanguage(value);
  };

  /**
   * Invoked when the user selects a specific time zone.
   */
  const onSetTimeZonePreference = function({value}): void {
    setSelectedTimeZone(value?.value);
  };

  // invokes when the user selects a specific time format...
  const onSetTimeFormatPreference = function(value): void {
    setDefaultTimeFormat(value !== NONE_VALUE ? value : null);
  };

  /**
   * Invoked when the user selects a specific Notification rows count.
   */
  const onSetNotificationRows = function(value) {
    setNotificationRows(value);
  };

  /**
   * Invoked when the user chooses to enable/disable notifications displaying prefrence
   */
  const onToggleShowNotifications = function({target:{checked}}: ChangeEvent<HTMLInputElement>) {
    setShowNotificationsToggle(checked);
    if (!checked) {
      setShowUrgentNotificationsCheckbox(false);
      setShowGeneralNotificationsCheckbox(false);
      setShowTimeRelatedNotificationsCheckbox(false);
      setShowConnectivityNotificationsCheckbox(false);
      setShowScheduledEventsNotificationsCheckbox(false);
    }
  };

  /**
   * Invoked when the user chooses to reset their default page preference to the
   * application's default page.
   */
  const onToggleGridSettings = function({target:{checked}}: ChangeEvent<HTMLInputElement>) {
    setGridSettingsToggle(checked);
  };

  /**
   * Invoked when the user chooses which types of notifications to display
   */
  const onToggleNotificationsSetting = function({target:{checked, name}}: ChangeEvent<HTMLInputElement>) {
    switch (name) {
      case Preference.showUrgentNotifications:
        setShowUrgentNotificationsCheckbox(checked);
        break;
      case Preference.showGeneralNotifications:
        setShowGeneralNotificationsCheckbox(checked);
        break;
      case Preference.showTimeRelatedNotifications:
        setShowTimeRelatedNotificationsCheckbox(checked);
        break;
      case Preference.showConnectivityNotifications:
        setShowConnectivityNotificationsCheckbox(checked);
        break;
      case Preference.showScheduledEventsNotifications:
        setShowScheduledEventsNotificationsCheckbox(checked);
        break;
      default:
        break;
    }
  };

  const ToggleTooltip = function(props) {
    return <Tooltip arrow classes={toggleTooltip()} {...props} />;
  };

  return (
    <div className={classes.container}>
      <FormSection title={translate('common:editors.preferences.title')}
        bottomBorder={bottomBorder}>
        <FormItem>
          <GroupChooser account={user.account}
            includeGroupProperties={includeGroupProperties}
            fieldLabel={translate('common:editors.shared.defaultGroup')}
            onChange={onSelectDefaultGroup}
            defaultValue={selectedDefaultGroup}
            value={selectedDefaultGroup} />
          {defaultPageLabel && (
            <TertiaryButton onClick={onResetDefaultGroup}
              fontSize='12px'>
              {translate('common:editors.preferences.page.reset', {page: EntityLabel.get(user.account)})}
            </TertiaryButton>
          )}
        </FormItem>
        {ctcAdmin ? <FormItem>
          <DropdownGroup fieldLabel={translate('common:editors.preferences.page.title')}
            groups={getCTCPageOptions(pages, pageNameResolver)}
            onChange={onSetGroupPagePreference}
            value={selectedPageId} />
          {defaultPageLabel && (
            <TertiaryButton onClick={onResetPagePreference}
              fontSize='12px'>
              {translate('common:editors.preferences.page.reset', {page: defaultPageLabel})}
            </TertiaryButton>
          )}
        </FormItem> : <FormItem>
          <Dropdown fieldLabel={translate('common:editors.preferences.page.title')}
            options={getPageOptions({pages, getRouteLabel: pageNameResolver})}
            onChange={onSetPagePreference}
            labelAsValue={false}
            value={selectedPageId} />
          {defaultPageLabel && (
            <TertiaryButton onClick={onResetPagePreference}
              fontSize='12px'>
              {translate('common:editors.preferences.page.reset', {page: defaultPageLabel})}
            </TertiaryButton>
          )}
        </FormItem>
        }
        <FormItem>
          <UnitOfMeasureChooser defaultValue={selectedUOM as Unit}
            onChange={onSetUOMPreference}
            useGroupDefaultOption />
        </FormItem>
        {!omitPreferences && (
          <FormItem>
            <LanguageChooser useGroupDefault
              defaultValue={selectedLanguage}
              onChange={onSetLanguagePreference}
              useForm />
            <span className={classes.infoLabel}>
              {translate('common:editors.preferences.language.info')}
            </span>
          </FormItem>
        )}
        <FormItem>
          <TimezoneChooser includeNone
            noneOptionLabel={translate('timezones:fields.groupDefault')}
            defaultValue={selectedTimeZone}
            onChange={onSetTimeZonePreference} />
        </FormItem>
        {includeDateFormat && (
          <FormItem>
            <DateFormatChooser includeNone
              includeNoneLabel={translate('common:editors.dateFormat.groupDefault')}
              value={defaultDateFormat}
              defaultValue={savedDateFormat || NONE_VALUE}
              onChange={onSetDateFormatPreference} />
          </FormItem>
        )}
        {includeTimeFormat && (
          <FormItem>
            <TimeFormatChooser includeNone
              includeNoneLabel={translate('common:timeFormat.groupDefault')}
              value={defaultTimeFormat}
              defaultValue={savedTimeFormat || NONE_VALUE}
              onChange={onSetTimeFormatPreference} />
          </FormItem>
        )}
        {isNotifications && <FormItem>
          <NotifictionRowsChooser onChange={onSetNotificationRows}
            fieldLabel={translate('common:editors.preferences.notification.label')}
            defaultValue={notificationRows} />
        </FormItem>}
        {!omitPreferences && <FormItem fullWidth>
          <div className={classes.preferencesPanel}>
            <div style={{display: 'flex'}}>
              <BodyTypography className={classes.preferencesTitle}>
                {translate('common:editors.preferences.showNotifications.title')}
              </BodyTypography>
              <ToggleTooltip title={translate('common:editors.preferences.showNotifications.tooltip')}>
                <div className={classes.prefernecesTooltip}>
                  <TopNavHelpIcon/>
                </div>
              </ToggleTooltip>
            </div>
            <Switch disabled={isUrgentNotificationsEnabled}
              onChange={onToggleShowNotifications}
              checked={showNotifications} />
          </div>
          {showNotifications && (
            <div className={classes.preferencesPanelExpanded}>
              <div className={classes.preferencesPanelRow}>
                <div className={classes.preferencesPanelCheckbox}>
                  <CheckBox label={translate('common:alertSeverity.Critical')}
                    name={Preference.showUrgentNotifications}
                    onChange={onToggleNotificationsSetting}
                    checked={showUrgentNotifications}
                    disabled={isUrgentNotificationsEnabled} />
                </div>
                <div className={classes.preferencesPanelCheckbox}>
                  <CheckBox label={translate('common:alertSeverity.High')}
                    name={Preference.showGeneralNotifications}
                    onChange={onToggleNotificationsSetting}
                    checked={showGeneralNotifications} />
                </div>
              </div>
              <div className={classes.preferencesPanelRow}>
                <div className={classes.preferencesPanelCheckbox}>
                  <CheckBox label={translate('common:alertSeverity.Medium')}
                    name={Preference.showTimeRelatedNotifications}
                    onChange={onToggleNotificationsSetting}
                    checked={showTimeRelatedNotifications} />
                </div>
                <div className={classes.preferencesPanelCheckbox}>
                  <CheckBox label={translate('common:alertSeverity.Low')}
                    name={Preference.showConnectivityNotifications}
                    onChange={onToggleNotificationsSetting}
                    checked={showConnectivityNotifications} />
                </div>
              </div>
              <div className={classes.preferencesPanelRow}>
                <div className={classes.preferencesPanelCheckbox}>
                  <CheckBox label={translate('common:alertSeverity.System')}
                    name={Preference.showScheduledEventsNotifications}
                    onChange={onToggleNotificationsSetting}
                    checked={showScheduledEventsNotifications} />
                </div>
              </div>
            </div>
          )}
        </FormItem>}
        <FormItem fullWidth>
          <div className={classes.preferencesPanel}>
            <div style={{display: 'flex'}}>
              <BodyTypography className={classes.preferencesTitle}>
                {translate('common:editors.preferences.grid')}
              </BodyTypography>
              <ToggleTooltip title={translate('common:editors.preferences.gridTooltip')}>
                <div className={classes.prefernecesTooltip}>
                  <TopNavHelpIcon/>
                </div>
              </ToggleTooltip>
            </div>
            <Switch disabled={false}
              onChange={onToggleGridSettings}
              checked={saveGridSettings} />
          </div>
        </FormItem>
      </FormSection>
    </div>
  );
});

export default UserPreferencesEditor;
